import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Trash2, Search, ArrowLeft } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface SavedSearch {
  id: string;
  statement: string;
  year_from: number | null;
  year_to: number | null;
  study_type: string | null;
  created_at: string;
}

export default function SavedSearches() {
  const [user, setUser] = useState<User | null>(null);
  const [searches, setSearches] = useState<SavedSearch[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();

  useEffect(() => {
    let mounted = true;
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      
      if (!session) {
        navigate('/auth');
        return;
      }
      
      setUser(session.user);
      loadSavedSearches();
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      
      if (!session) {
        navigate('/auth');
      } else {
        setUser(session.user);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  const loadSavedSearches = async () => {
    try {
      const { data, error } = await supabase
        .from('saved_searches')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSearches(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar las búsquedas guardadas",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('saved_searches')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setSearches(searches.filter(s => s.id !== id));
      toast({
        title: "Búsqueda eliminada",
        description: "La búsqueda ha sido eliminada correctamente",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar la búsqueda",
        variant: "destructive",
      });
    }
  };

  const handleSearch = (search: SavedSearch) => {
    navigate('/', { 
      state: { 
        statement: search.statement,
        yearFrom: search.year_from,
        yearTo: search.year_to,
        studyType: search.study_type
      } 
    });
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Cargando...</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Búsquedas Guardadas - MedAI Cite</title>
        <meta name="description" content="Gestiona tus búsquedas médicas guardadas en MedAI Cite. Área privada de usuario." />
        <link rel="canonical" href="https://medai-cite.com/saved-searches" />
      </Helmet>
      
      <main className="min-h-screen bg-gradient-to-br from-background to-muted/20">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t.backToSearch}
          </Button>
          <h1 className="text-4xl font-bold mb-2">{t.savedSearchesTitle}</h1>
          <p className="text-muted-foreground">
            {t.noSavedSearchesDescription}
          </p>
        </div>

        {searches.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground mb-4">
                {t.noSavedSearches}
              </p>
              <Button onClick={() => navigate('/')}>
                {t.backToSearch}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {searches.map((search) => (
              <Card key={search.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg leading-tight">
                    {search.statement}
                  </CardTitle>
                  <CardDescription>
                    Guardado el {new Date(search.created_at).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {search.year_from && (
                      <span className="text-sm bg-muted px-2 py-1 rounded">
                        Desde: {search.year_from}
                      </span>
                    )}
                    {search.year_to && (
                      <span className="text-sm bg-muted px-2 py-1 rounded">
                        Hasta: {search.year_to}
                      </span>
                    )}
                    {search.study_type && (
                      <span className="text-sm bg-muted px-2 py-1 rounded">
                        {search.study_type}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleSearch(search)}
                      className="flex-1"
                    >
                      <Search className="mr-2 h-4 w-4" />
                      {t.rerun}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => handleDelete(search.id)}
                      aria-label="Delete search"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </main>
    </>
  );
}
